import { useEffect, useState, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { DIMENSOES, fmtNota, formativaBadge } from "@/lib/constants";
import { ArrowLeft, Loader2, FileSpreadsheet, FileText, Trophy, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function FormativaConsolidado() {
  const { turmaId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [openAluno, setOpenAluno] = useState(null);

  useEffect(() => {
    api.get(`/formativa/consolidado?turma_id=${turmaId}`).then((r) => setData(r.data));
  }, [turmaId]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const criterios = data.criterios || [];
  const exportar = (fmt) => {
    const base = (data.turma?.nome || "turma").replace(/\s+/g, "_");
    downloadFile(`/formativa/consolidado/export/${fmt}?turma_id=${turmaId}`, `MÉTRON_Formativa_Consolidado_${base}.${fmt}`)
      .catch(() => toast.error("Falha ao exportar"));
  };

  return (
    <div>
      <button data-testid="fcons-voltar" onClick={() => navigate("/formativa")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </button>
      <PageHeader title="Consolidado da Turma" subtitle={`${data.curso_nome || ""} · ${data.turma?.nome || ""} · média final = média entre instrutores`}>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="fcons-export-xlsx" onClick={() => exportar("xlsx")}>
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
          </Button>
          <Button variant="outline" size="sm" data-testid="fcons-export-docx" onClick={() => exportar("docx")}>
            <FileText className="h-4 w-4 mr-2" /> Word
          </Button>
        </div>
      </PageHeader>

      {/* Ranking */}
      <Card className="p-5 bg-card border-border mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="h-5 w-5 text-primary" />
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight">Ranking</h2>
        </div>
        {!data.ranking.length ? (
          <p className="text-sm text-muted-foreground">Nenhuma avaliação registrada ainda.</p>
        ) : (
          <div className="space-y-2">
            {data.ranking.map((a, i) => (
              <div key={a.aluno_id} data-testid={`frank-${a.aluno_id}`} className="flex items-center gap-3">
                <div className="w-7 text-center font-heading font-bold text-muted-foreground">{i + 1}º</div>
                <div className="flex-1 min-w-0 truncate font-medium">{a.aluno_nome}</div>
                <Badge className={formativaBadge(a.media_final)}>{fmtNota(a.media_final)}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Tabela por aluno (expandível: instrutores) */}
      <Card className="bg-card border-border overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-border font-heading font-bold uppercase tracking-tight text-sm">Por aluno</div>
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Aluno</TableHead>
              <TableHead className="text-center">Avaliadores</TableHead>
              {Object.entries(DIMENSOES).map(([k, d]) => (
                <TableHead key={k} className="text-center hidden md:table-cell">{d.label}</TableHead>
              ))}
              <TableHead className="text-right">Média final</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.alunos.map((al) => (
              <Fragment key={al.aluno_id}>
                <TableRow data-testid={`fcons-aluno-${al.aluno_id}`} className="border-border cursor-pointer"
                  onClick={() => setOpenAluno(openAluno === al.aluno_id ? null : al.aluno_id)}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-1">
                      {openAluno === al.aluno_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {al.aluno_nome}
                    </span>
                  </TableCell>
                  <TableCell className="text-center text-muted-foreground">{al.n_avaliadores}</TableCell>
                  {Object.keys(DIMENSOES).map((k) => (
                    <TableCell key={k} className="text-center hidden md:table-cell">
                      {fmtNota(al.media_por_dimensao?.[k]?.media)}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <Badge className={formativaBadge(al.media_final)}>{fmtNota(al.media_final)}</Badge>
                  </TableCell>
                </TableRow>
                {openAluno === al.aluno_id && (
                  <TableRow className="border-border bg-secondary/30 hover:bg-secondary/30">
                    <TableCell colSpan={3 + Object.keys(DIMENSOES).length}>
                      {!al.avaliadores.length ? (
                        <p className="text-xs text-muted-foreground py-2">Sem avaliações deste aluno.</p>
                      ) : (
                        <div className="overflow-x-auto py-2">
                          <table className="text-xs w-full min-w-[600px]">
                            <thead>
                              <tr className="text-muted-foreground">
                                <th className="text-left font-medium pr-3 py-1">Instrutor</th>
                                <th className="text-left font-medium pr-3">Status</th>
                                {criterios.map((c) => (
                                  <th key={c.id} className="px-1 py-1 font-medium" title={c.nome}>{c.nome.slice(0, 12)}…</th>
                                ))}
                                <th className="px-2 font-medium">Soma</th>
                                <th className="px-2 font-medium">Média</th>
                              </tr>
                            </thead>
                            <tbody>
                              {al.avaliadores.map((av, idx) => (
                                <tr key={idx} className="border-t border-border/50">
                                  <td className="pr-3 py-1.5 font-medium whitespace-nowrap">{av.avaliador_nome}</td>
                                  <td className="pr-3">
                                    {av.status === "concluida"
                                      ? <span className="text-teal-300">Concluída</span>
                                      : <span className="text-orange-300">Em andamento</span>}
                                  </td>
                                  {criterios.map((c) => (
                                    <td key={c.id} className="text-center px-1">{fmtNota(av.notas?.[c.id])}</td>
                                  ))}
                                  <td className="text-center px-2 font-mono">{fmtNota(av.soma)}</td>
                                  <td className="text-center px-2">
                                    <Badge className={`${formativaBadge(av.media)} text-[10px]`}>{fmtNota(av.media)}</Badge>
                                  </td>
                                </tr>
                              ))}
                              <tr className="border-t border-border font-semibold">
                                <td className="pr-3 py-1.5" colSpan={2}>Média por critério</td>
                                {criterios.map((c) => (
                                  <td key={c.id} className="text-center px-1">{fmtNota(al.media_por_criterio?.[c.id])}</td>
                                ))}
                                <td></td>
                                <td className="text-center px-2">
                                  <Badge className={`${formativaBadge(al.media_final)} text-[10px]`}>{fmtNota(al.media_final)}</Badge>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
            {!data.alunos.length && (
              <TableRow><TableCell colSpan={3 + Object.keys(DIMENSOES).length} className="text-center py-10 text-muted-foreground">Nenhum aluno nesta turma.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Média geral da turma por critério */}
      <Card className="bg-card border-border overflow-hidden">
        <div className="px-5 py-3 border-b border-border font-heading font-bold uppercase tracking-tight text-sm">Média da turma por critério</div>
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Critério</TableHead>
              <TableHead className="hidden sm:table-cell">Dimensão</TableHead>
              <TableHead className="text-right">Média da turma</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {criterios.map((c) => (
              <TableRow key={c.id} className="border-border">
                <TableCell className="font-medium">{c.nome}</TableCell>
                <TableCell className="hidden sm:table-cell">
                  <Badge className={`${DIMENSOES[c.dimensao]?.badge} text-[10px]`}>{DIMENSOES[c.dimensao]?.label}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Badge className={formativaBadge(data.media_geral_criterio?.[c.id])}>{fmtNota(data.media_geral_criterio?.[c.id])}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
